import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DataTableSort } from "./DataTable";
import { FilterSelect } from "./FilterControls";

type MobileSortOption<K extends string> = { key: K; label: string };

/**
 * Tri des cartes sous `md`, où il n'y a pas d'en-têtes de colonnes à cliquer. Repliable
 * comme le « Filtrer par » des historiques ; le tri choisi reste lisible replié.
 */
export const MobileSort = <K extends string>({
  options,
  sort,
  onChange,
}: {
  options: MobileSortOption<K>[];
  sort?: DataTableSort<K>;
  onChange: (sort?: DataTableSort<K>) => void;
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const optionLabel = (
    option: MobileSortOption<K>,
    direction: "asc" | "desc"
  ) =>
    direction === "asc"
      ? t("sort-asc", { column: option.label })
      : t("sort-desc", { column: option.label });
  const current = options.find((option) => option.key === sort?.key);

  const select = (value: string) => {
    const [key, direction] = value.split(":");
    const option = options.find((o) => o.key === key);
    onChange(
      option
        ? { key: option.key, direction: direction === "desc" ? "desc" : "asc" }
        : undefined
    );
  };

  return (
    <div className="md:hidden mb-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex items-center gap-2 text-default-text font-medium"
      >
        <FontAwesomeIcon icon="sort" />
        {t("sort-by")}
        {sort && current && (
          <span className="text-sm font-normal text-grey-readable">
            · {optionLabel(current, sort.direction)}
          </span>
        )}
      </button>

      {open && (
        <div className="mt-3">
          <FilterSelect
            aria-label={t("sort-by")}
            value={sort ? `${sort.key}:${sort.direction}` : ""}
            onChange={(e) => select(e.target.value)}
          >
            <option value="">{t("sort-default")}</option>
            {options.flatMap((option) =>
              (["asc", "desc"] as const).map((direction) => (
                <option
                  key={`${option.key}:${direction}`}
                  value={`${option.key}:${direction}`}
                >
                  {optionLabel(option, direction)}
                </option>
              ))
            )}
          </FilterSelect>
        </div>
      )}
    </div>
  );
};

/** Options du tri mobile : les colonnes triables du tableau, avec leur en-tête. */
export const sortOptions = <K extends string>(
  columns: { key: K; header: unknown; sortable?: boolean }[]
): MobileSortOption<K>[] =>
  columns.flatMap((column) =>
    column.sortable && typeof column.header === "string"
      ? [{ key: column.key, label: column.header }]
      : []
  );
